import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const SCROLL_STORAGE_KEY = 'vibary_scroll_positions';
const MAX_ENTRIES = 50; // Prevent unbounded growth

/**
 * Hook to manage scroll restoration for browser navigation.
 *
 * - Saves scroll position when navigating away
 * - Restores scroll position on back/forward navigation (POP)
 * - Scrolls to top on forward navigation (PUSH)
 * - Uses sessionStorage to persist across page refreshes
 */
export function useScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const isFirstRender = useRef(true);
  const lastKey = useRef(location.key);

  // Get scroll positions from sessionStorage
  const getScrollPositions = useCallback(() => {
    try {
      const stored = sessionStorage.getItem(SCROLL_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }, []);

  // Save scroll positions to sessionStorage
  const saveScrollPositions = useCallback((positions) => {
    try {
      // Prune old entries if we exceed max
      const keys = Object.keys(positions);
      if (keys.length > MAX_ENTRIES) {
        const sortedKeys = keys.slice(0, keys.length - MAX_ENTRIES);
        sortedKeys.forEach(key => delete positions[key]);
      }
      sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(positions));
    } catch {
      // sessionStorage might be full or unavailable
    }
  }, []);

  // Save current scroll position before navigating away
  const saveCurrentScrollPosition = useCallback(() => {
    const key = lastKey.current;
    if (!key || key === 'default') return;

    const positions = getScrollPositions();
    positions[key] = window.scrollY;
    saveScrollPositions(positions);
  }, [getScrollPositions, saveScrollPositions]);

  // Save scroll position before unload and before navigation
  useEffect(() => {
    // Save on beforeunload (browser refresh/close)
    const handleBeforeUnload = () => {
      saveCurrentScrollPosition();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveCurrentScrollPosition]);

  // Handle scroll restoration/reset on route change
  useEffect(() => {
    // Skip first render - let the browser handle initial scroll
    if (isFirstRender.current) {
      isFirstRender.current = false;
      lastKey.current = location.key;
      return;
    }

    // Save scroll position of the page we're leaving
    saveCurrentScrollPosition();

    // Update the key for the new location
    lastKey.current = location.key;

    // Determine scroll behavior based on navigation type
    if (navigationType === 'POP') {
      // Back/forward navigation - restore scroll position
      const positions = getScrollPositions();
      const savedPosition = positions[location.key];

      if (savedPosition !== undefined) {
        // Use requestAnimationFrame to ensure the DOM has updated
        requestAnimationFrame(() => {
          window.scrollTo(0, savedPosition);
        });
      }
      // If no saved position, let browser handle it (usually stays at current position)
    } else {
      // PUSH or REPLACE navigation - scroll to top
      window.scrollTo(0, 0);
    }
  }, [location.key, location.pathname, navigationType, getScrollPositions, saveCurrentScrollPosition]);
}

export default useScrollRestoration;
